import {
  Button,
  Link,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderTabs,
  PageHeaderTitle,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { Outlet, useLocation } from '@tanstack/react-router';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useAnalytics } from '@/services/analytics';
import { useDeleteActionCredential } from '@/services/db/action-credential/mutations';
import { PushDocumentTitle } from './components/push-document-title';
import { useDisablePushConfirmation } from './components/push-confirmations';
import { PushTabNav } from './components/push-tab-nav';
import { firstPushErrorMessage, REMOVE_FAILED_MESSAGE } from './utils/push-errors';
import { usePushBreadcrumbs, usePushPermissions, usePushPlatforms, usePushSettingsViewedAnalytics } from './hooks';

const PUSH_DOCS_URL = 'https://docs.customer.io/journeys/push-getting-started/';

type DisableAllPushActionProps = {
  workspaceId: number;
  canDelete: boolean;
};

function DisableAllPushAction({ workspaceId, canDelete }: DisableAllPushActionProps) {
  const { track } = useAnalytics();
  const snackbars = useSnackbars();
  const removeFcm = useDeleteActionCredential({ workspaceId });

  const openConfirmation = useDisablePushConfirmation({
    platform: null,
    onDisable: async () => {
      try {
        await removeFcm.mutateAsync({ actionType: 'push_fcm' });
      } catch (error) {
        // The header sits above both tabs and has no banner list of its own, so
        // the one place left to say the key is still there is a snackbar.
        snackbars.create({
          variant: 'critical',
          id: 'push.disable.failed',
          message: firstPushErrorMessage(error) ?? REMOVE_FAILED_MESSAGE,
        });
        return;
      }
      void track('push', 'pushDisabled', { platform: 'iOS', provider: 'FCM' });
      void track('push', 'pushDisabled', { platform: 'Android', provider: 'FCM' });
    },
  });

  return (
    <Button
      variant="secondary"
      isCritical={true}
      isDisabled={!canDelete}
      onClick={openConfirmation}
      data-test-disable-push-button
    >
      Disable push…
    </Button>
  );
}

export default function WorkspacePushSettingsPage() {
  const { pathname } = useLocation();
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const { canDelete } = usePushPermissions();
  const { platforms, isPending } = usePushPlatforms(workspaceId ?? undefined);

  usePushBreadcrumbs(workspaceId);
  usePushSettingsViewedAnalytics(platforms, !isPending);

  // When one Firebase key is the workspace's whole push setup, removing it belongs to
  // the page; a platform on its own Apple key is turned off from its tab.
  const isSingleSharedKey = platforms.length > 1 && platforms.every((entry) => entry.provider === 'fcm');

  return (
    <Page>
      <PushDocumentTitle />
      <PageHeader>
        <PageHeaderTitle>Push notifications</PageHeaderTitle>
        <PageHeaderDescription>
          Configure your app to send push notifications to your customers' Android or iOS devices.{' '}
          <Link href={PUSH_DOCS_URL} variant="secondary" isExternal={true}>
            Learn more about Customer.io Push
          </Link>
          .
        </PageHeaderDescription>
        <PageHeaderActions>
          {isSingleSharedKey && workspaceId != null ? (
            <DisableAllPushAction workspaceId={workspaceId} canDelete={canDelete} />
          ) : null}
        </PageHeaderActions>
        <PageHeaderTabs>
          <PushTabNav workspaceId={workspaceId} platforms={platforms} pathname={pathname} />
        </PageHeaderTabs>
      </PageHeader>
      <PageBody>
        <Outlet />
      </PageBody>
    </Page>
  );
}
